import { useState } from "react";
import type {
  CareCaseChangeReason,
  CareCaseTransitionResult,
  PatientGroup,
} from "@care-by-ella/contracts";
import { CARE_CASE_CHANGE_REASONS } from "@care-by-ella/contracts";
import { transitionCareCase, UnauthorizedError } from "@/lib/clinician-api";
import {
  CARE_CASE_NEXT_STEP_LABEL,
  CARE_CASE_REASON_LABEL,
  requiresImmediateAction,
} from "@/lib/care-case-labels";
import { hiddenWhenReadOnly } from "./SupportView";

// TES-869 (Ö-209, Ö-210) — the doctor-driven care-case correction.
//
// TES-862 shipped the endpoint and nothing called it: `apps/admin` had zero
// hits on `care-case`, so a correction the server supports in both directions
// could not be made by anyone.
//
// TWO DESIGN CHOICES WORTH THE WORDS.
//
// 1. The DIRECTION is derived, never chosen. There are only two groups; a
//    picker would let a doctor select the one she is already in, and the
//    server would refuse — a validation error standing in for a question the
//    UI should never have asked.
//
// 2. The ROUTING is shown back. Ö-210:
//    "Systemet ska skapa nästa saknade riktiga steg, inte starta om onboarding eller automatiskt kräva ett nytt möte som redan har ägt rum."
//    The server decides that, and this shows what
//    it decided — including which packages the correction withdrew. A
//    correction that silently closes a patient's open work is the kind of
//    thing a doctor should see the moment he does it, not discover later.
function CareCaseCorrectionForm({
  patientId,
  patientGroup,
  onCorrected,
  onUnauthorized,
}: {
  patientId: string;
  patientGroup: PatientGroup;
  onCorrected: () => void;
  onUnauthorized?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState<CareCaseChangeReason | "">("");
  const [reasonText, setReasonText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CareCaseTransitionResult | null>(null);

  const toTransfer = patientGroup !== "PRESCRIPTION_TRANSFER";
  const transition = toTransfer
    ? ("NEW_TREATMENT_TO_PRESCRIPTION_TRANSFER" as const)
    : ("PRESCRIPTION_TRANSFER_TO_NEW_TREATMENT" as const);
  const target = toTransfer ? "receptövertag" : "ny behandling";

  // `other` carries nothing on its own — the same rule the contract enforces,
  // mirrored here so the doctor is stopped before the round trip rather than
  // by a 400 afterwards.
  const needsText = reasonCode === "other";
  const canSubmit =
    reasonCode !== "" && (!needsText || reasonText.trim().length > 0) && !busy;

  async function submit() {
    if (reasonCode === "") return;
    setBusy(true);
    setError(null);
    try {
      const res = await transitionCareCase(patientId, {
        transition,
        reasonCode,
        ...(reasonText.trim() ? { reasonText: reasonText.trim() } : {}),
      });
      setResult(res);
      setOpen(false);
      onCorrected();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-md border border-surfaceMuted bg-surface p-md">
      <h2 className="mb-xs font-display text-lg">Ärendetyp</h2>
      <p className="text-sm text-textSecondary">
        Patienten ligger på{" "}
        <strong className="text-textPrimary">
          {toTransfer ? "ny behandling" : "receptövertag"}
        </strong>
        .
      </p>

      {result ? (
        // The readback. Point 2 of the issue, and the half that matters: what
        // the correction withdrew, and what she owes now.
        <div className="mt-sm rounded-sm border border-brand/30 bg-brand/5 p-sm">
          <p className="text-sm text-textPrimary">
            Ärendet ligger nu på{" "}
            <strong>
              {result.patientGroup === "PRESCRIPTION_TRANSFER"
                ? "receptövertag"
                : "ny behandling"}
            </strong>
            .
          </p>
          <p className="mt-xs text-sm text-textSecondary">
            {CARE_CASE_NEXT_STEP_LABEL[result.routing.nextStep]}
          </p>
          {result.routing.closedAssignmentIds.length > 0 ? (
            <p className="mt-xs text-xs text-textSecondary">
              {result.routing.closedAssignmentIds.length} pågående{" "}
              {result.routing.closedAssignmentIds.length === 1
                ? "paket stängdes"
                : "paket stängdes"}{" "}
              av korrigeringen.
            </p>
          ) : null}
          {requiresImmediateAction(result.routing.nextStep) &&
          result.routing.missingEvidenceTasks.length > 0 ? (
            // Case B. The doctor picks these in the transfer review's targeted
            // komplettering (TES-763) rather than sending her through all of
            // F-07 again — naming them here is the handover to that view.
            <p className="mt-xs text-xs text-warning">
              Saknas: {result.routing.missingEvidenceTasks.join(", ")}. Välj dem
              som riktad komplettering i granskningen.
            </p>
          ) : null}
        </div>
      ) : null}

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-sm rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:bg-surfaceMuted"
        >
          Korrigera till {target}
        </button>
      ) : (
        <div className="mt-sm flex flex-col gap-sm">
          <label className="text-xs text-textSecondary" htmlFor="cc-reason">
            Orsak
          </label>
          <select
            id="cc-reason"
            value={reasonCode}
            onChange={(e) =>
              setReasonCode(e.target.value as CareCaseChangeReason | "")
            }
            className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm"
          >
            <option value="">Välj orsak…</option>
            {CARE_CASE_CHANGE_REASONS.map((r) => (
              <option key={r} value={r}>
                {CARE_CASE_REASON_LABEL[r]}
              </option>
            ))}
          </select>

          <label className="text-xs text-textSecondary" htmlFor="cc-text">
            {needsText ? "Ange orsak (obligatoriskt)" : "Kommentar (frivillig)"}
          </label>
          <textarea
            id="cc-text"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            rows={2}
            className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm"
          />

          {error ? <p className="text-xs text-danger">{error}</p> : null}

          <div className="flex items-center gap-sm">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!canSubmit}
              className="rounded-md bg-brand px-md py-sm text-sm text-textOnBrand hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Korrigerar…" : `Korrigera till ${target}`}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
              disabled={busy}
              className="rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:bg-surfaceMuted"
            >
              Avbryt
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// TES-1040 — hidden in the admin's read-only support view: a care-case correction is a write.
export const CareCaseCorrection = hiddenWhenReadOnly(CareCaseCorrectionForm);
